import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutPropertiesLoose from '@babel/runtime/helpers/objectWithoutPropertiesLoose';
import { Component, createElement, createContext } from 'react';
import { StyleSheet, css } from 'aphrodite';

function flatten(list) {
  const result = [];

  if (!list) {
    return result;
  } else if (Array.isArray(list)) {
    for (const item of list) {
      result.push.apply(result, flatten(item));
    }
  } else {
    result.push(list);
  }

  return result;
}

function processStyleList(style) {
  const stylesheetStyles = [];
  const inlineStyles = [];

  if (!style) {
    return {
      style: {},
      className: ""
    };
  } // Check to see if we should inline all the styles for snapshot tests.


  const shouldInlineStyles = typeof global !== "undefined" && global.SNAPSHOT_INLINE_APHRODITE;
  flatten(style).forEach(child => {
    // Check for aphrodite internal property
    const _definition = child._definition;

    if (_definition != null) {
      if (shouldInlineStyles) {
        const def = {}; // React 16 complains about invalid keys in inline styles.
        // It doesn't accept kebab-case in media queries and instead
        // prefers camelCase.

        for (const [key, value] of Object.entries(_definition)) {
          // This regex converts all instances of -{lowercaseLetter}
          // to the uppercase version of that letter, without the
          // leading dash.
          def[key.replace(/-[a-z]/g, match => match[1].toUpperCase())] = value;
        }

        inlineStyles.push(def);
      } else {
        stylesheetStyles.push(child);
      }
    } else {
      inlineStyles.push(child);
    }
  });
  const inlineStylesObject = Object.assign.apply(Object, [{}].concat(inlineStyles)); // TODO(somewhatabstract): When aphrodite no longer puts "!important" on
  // all the styles, remove this <ADD JIRA ISSUE HERE IF THIS PASSES REVIEW>
  // If we're not snapshotting styles, let's create a class for the inline
  // styles so that they can apply to the element even with aphrodite's
  // use of !important.

  if (inlineStyles.length > 0 && !shouldInlineStyles) {
    const inlineStylesStyleSheet = StyleSheet.create({
      inlineStyles: inlineStylesObject
    });
    stylesheetStyles.push(inlineStylesStyleSheet.inlineStyles);
  }

  return {
    style: shouldInlineStyles ? inlineStylesObject : {},
    className: css.apply(void 0, stylesheetStyles)
  };
}

const _excluded = ["children", "style", "tag", "testId"];
const isHeaderRegex = /^h[1-6]$/;
const styles = StyleSheet.create({
  text: {
    // Disable subpixel antialiasing on Mac desktop for consistency of
    // rendering with mobile and Sketch (neither of which support it).
    // See https://bjango.com/articles/subpixeltext/ for more details.
    WebkitFontSmoothing: "antialiased",
    MozOsxFontSmoothing: "grayscale"
  },
  header: {
    // User agent stylesheets add vertical margins to header tags by
    // default. We prefer to be more deliberate in our spacing instead.
    marginTop: 0,
    marginBottom: 0
  }
});
/**
 * Text is a building block for constructing other components. `Text` roughly
 * maps to `span`. You can override which tag is used to render the component
 * (for semantic purposes) by specifying the `tag` prop.
 *
 * These components can take styles (via the `style` prop) in a variety of
 * manners:
 *
 * - An inline style object
 * - An `aphrodite` StyleSheet style
 * - An array combining the above
 */

class Text extends Component {
  render() {
    const _this$props = this.props,
          {
      children,
      style,
      tag: Tag,
      testId
    } = _this$props,
          otherProps = _objectWithoutPropertiesLoose(_this$props, _excluded);

    const isHeader = isHeaderRegex.test(Tag);
    const styleAttributes = processStyleList([styles.text, isHeader && styles.header, style]);
    return /*#__PURE__*/createElement(Tag, _extends({}, otherProps, {
      style: styleAttributes.style,
      className: styleAttributes.className,
      "data-test-id": testId
    }), children);
  }

}
Text.defaultProps = {
  tag: "span"
};

const _excluded$1 = ["className", "style"];
// TODO(kevinb): have an a version which uses exact object types
function addStyle(Component, defaultStyle) {
  function StyleComponent(props) {
    const {
      className,
      style
    } = props,
          tmpOtherProps = _objectWithoutPropertiesLoose(props, _excluded$1); // NOTE(jeresig): We need to cast the remaining props to be the right
    // value to ensure that they're typed properly.


    const otherProps = tmpOtherProps;
    const reset = typeof Component === "string" ? overrides[Component] : null;
    const {
      className: aphroditeClassName,
      style: inlineStyles
    } = processStyleList([reset, defaultStyle, style]);
    return /*#__PURE__*/createElement(Component, _extends({}, otherProps, {
      className: [aphroditeClassName, className].filter(Boolean).join(" "),
      style: inlineStyles
    }));
  }

  return StyleComponent;
}
/**
 * These are necessary to override various custom styles that browsers add so that
 * elements have consistent styles across all browsers.  Only add styles here if
 * they appear in https://github.com/necolas/normalize.css/blob/master/normalize.css.
 */

const overrides = StyleSheet.create({
  button: {
    margin: 0,
    // Safari adds 2px left/right margins
    "::-moz-focus-inner": {
      border: 0 // Firefox adds an inner focus ring around text

    }
  }
});

const _excluded$2 = ["testId", "tag"];
const styles$1 = StyleSheet.create({
  // https://github.com/facebook/css-layout#default-values
  default: {
    alignItems: "stretch",
    borderWidth: 0,
    borderStyle: "solid",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    margin: 0,
    padding: 0,
    position: "relative",
    zIndex: 0,
    // fix flexbox bugs
    minHeight: 0,
    minWidth: 0
  }
});
const StyledDiv = addStyle("div", styles$1.default);
const StyledArticle = addStyle("article", styles$1.default);
const StyledAside = addStyle("aside", styles$1.default);
const StyledNav = addStyle("nav", styles$1.default);
const StyledSection = addStyle("section", styles$1.default);
/**
 * View is a building block for constructing other components. `View` roughly
 * maps to `div` and `Text` roughly maps to `span`. You can override which tag
 * is used to render the component (for semantic purposes) by specifying the
 * `tag` prop.
 *
 * These components can take styles (via the `style` prop) in a variety of
 * manners:
 *
 * - An inline style object
 * - An `aphrodite` StyleSheet style
 * - An array combining the above
 */

class View extends Component {
  render() {
    const _this$props = this.props,
          {
      testId,
      tag
    } = _this$props,
          restProps = _objectWithoutPropertiesLoose(_this$props, _excluded$2);

    const props = _extends({}, restProps, {
      "data-test-id": testId
    });

    switch (tag) {
      case "article":
        return /*#__PURE__*/createElement(StyledArticle, props);

      case "aside":
        return /*#__PURE__*/createElement(StyledAside, props);

      case "nav":
        return /*#__PURE__*/createElement(StyledNav, props);

      case "section":
        return /*#__PURE__*/createElement(StyledSection, props);

      case "div":
        return /*#__PURE__*/createElement(StyledDiv, props);

      default:
        throw Error(`${tag} is not an allowed value for the 'tag' prop`);
    }
  }

}
View.defaultProps = {
  tag: "div"
};

/**
 * We use render functions so that we don't do any work unless we need to.
 * This avoids rendering but not mounting potentially complex component trees.
 */

/**
 * This is the context that tracks who is doing what in our SSR component tree.
 *
 * root:
 *   no one has instigated an initial SSR render so the component that sees
 *   this "root" state is responsible for controlling initial versus standard
 *   rendering semantics
 *
 * initial:
 *   this means the SSR render has started, and all SSR components should act
 *   as though they are on the server
 *
 * standard:
 *   means that we're all now doing non-SSR rendering
 */
const RenderStateContext = /*#__PURE__*/createContext("root");
/**
 * Defer or change rendering until the component did mount.
 *
 * The purpose of this component is to disable or modify serverside rendering
 * of certain components. Disabling rendering on the server, by itself, would
 * not be sufficient, since the initial render of the component must match
 * what is rendered on the server. Therefore, this component also disables
 * rendering the first time around on the client.
 *
 * If `WithSSRPlaceholder` components are nested within one another,
 * the root `WithSSRPlaceholder` component will handle the initial
 * render, but nested `WithSSRPlaceholder` components will delegate to
 * the root one, meaning that we don't cascade delayed rendering down
 * the component tree. This will also be the case across portal
 * boundaries.
 *
 * Example:
 *
 * ```js
 * <WithSSRPlaceholder placeholder={() => <div>Renders on the server!</div>}>
 *   {() => <div>Only renders on the client (after rehydration).</div>}
 * </WithSSRPlaceholder>
 * ```
 */

class WithSSRPlaceholder extends Component {
  constructor(...args) {
    super(...args);
    this.state = {
      mounted: false
    };
    this._isTheRootComponent = false;
  }

  componentDidMount() {
    if (this._isTheRootComponent) {
      // We only want to force a new render if we were responsible for
      // the first render, so we guard that state change here.
      // eslint-disable-next-line react/no-did-mount-set-state
      this.setState({
        mounted: true
      });
    }
  }

  _renderAsRootComponent() {
    const {
      mounted
    } = this.state;
    const {
      children,
      placeholder
    } = this.props; // We are the first component in the tree.
    // We are in control of instigating a second render for our
    // component tree.

    this._isTheRootComponent = true;

    if (mounted) {
      // This is our second non-SSR render, so let's tell everyone to
      // do their thing.
      return /*#__PURE__*/createElement(RenderStateContext.Provider, {
        value: "standard"
      }, children());
    } // OK, this is the very first render.
    // If we have a placeholder, we render it, and ensure that any
    // nested SSR components know we're still on that first render
    // but they're not in charge of instigating the second render.


    if (placeholder) {
      return /*#__PURE__*/createElement(RenderStateContext.Provider, {
        value: "initial"
      }, placeholder());
    } // Otherwise, we return nothing.


    return null;
  }

  _maybeRender(renderState) {
    const {
      children,
      placeholder
    } = this.props;

    switch (renderState) {
      // There are edge cases where for some reason, we get an unknown
      // context value here. So far it seems to be when we're nested in a
      // v1 WithSSRPlaceholder equivalent component, or in some older
      // React v16 situations where we're nested in the provider of a
      // different context.
      //
      // We ignore this from coverage. It's a maintenance case to help
      // us catch code changes that affect the control flow unexpectedly,
      // but it's not something we need to write a test case for.
      //

      /* istanbul ignore next */
      default:
        // Let's log this case so we can debug it easily.
        // Then fall through to the root case.

        /* eslint-disable-next-line no-console */
        console.log(`We got a render state we don't understand: "${JSON.stringify(renderState)}"`); // We "fallthrough" to the root case. This is more obvious
        // and maintainable code than just ignoring the no-fallthrough
        // lint rule.

        return this._maybeRender("root");

      case "root":
        return this._renderAsRootComponent();

      case "initial":
        // We're not the root component, so we just have to either
        // render our placeholder or nothing.
        // The second render is going to be triggered for us.
        if (placeholder) {
          return placeholder();
        } // Otherwise, we render nothing.


        return null;

      case "standard":
        // We have covered the SSR render, we're now rendering with
        // standard rendering semantics.
        return children();
    }
  }

  render() {
    return /*#__PURE__*/createElement(RenderStateContext.Consumer, null, value => this._maybeRender(value));
  }

}

/**
 * This is NOT for direct use. Instead, see the UniqueIDProvider component.
 *
 * Implements IIdentifierFactory to provide unique identifiers.
 */
class UniqueIDFactory {
  /**
   * Creates a UniqueIDFactory instance.
   *
   * @param {string} scope An optional case-insensitive scope for the
   * factory. This will be used as part of the identifier. Useful for
   * providing context to the identifiers, which can be useful in
   * differentiating elements when debugging the DOM. This must contain only
   * hyphen and alphanumeric characters.
   */
  constructor(scope) {
    this.get = key => {
      const normalizedKey = key.toLowerCase();

      if (!this._hasValidIdChars(key)) {
        throw new Error(`Invalid identifier key: ${key}`);
      }

      return `${this._uniqueFactoryName}-${normalizedKey}`;
    };

    scope = typeof scope === "string" ? scope : "";
    const normalizedScope = scope.toLowerCase();

    if (!this._hasValidIdChars(normalizedScope)) {
      throw new Error(`Invalid factory scope: ${scope}`);
    }

    this._uniqueFactoryName = `uid-${normalizedScope}-${UniqueIDFactory._factoryUniquenessCounter++}`;
  }
  /**
   * This method verifies that a string contains valid characters for an
   * identifier. It does not assert that a string IS a valid identifier (for
   * example, that it doesn't start with numbers). We don't need to do that
   * here because all identifiers are prefixed to avoid needing that check.
   */


  _hasValidIdChars(value) {
    if (typeof value !== "string") {
      return false;
    }

    const invalidCharsReplaced = value.replace(/[^\d\w-]/g, "-");
    return value === invalidCharsReplaced;
  }
  /**
   * Provides a unique identifier with the given key.
   *
   * @param {string} key The case-insensitive key of the identifier.
   *
   * @returns {string} A unique identifier that will remain the same for this
   * key in this factory. This must contain only hyphen and alphanumeric
   * characters.
   */


}
UniqueIDFactory._factoryUniquenessCounter = 0;

/**
 * This is NOT for direct use. Instead, see the UniqueIDProvider component.
 *
 * Implements a version of IIdentifierFactory that can be used for providing
 * identifiers on initial render of components that are eligible for server-side
 * rendering.
 *
 * The identifiers are not guaranteed to be unique, but they will match between
 * server and the first client render.
 */
class SsrIDFactory {
  get(id) {
    return id;
  }

}

SsrIDFactory.Default = new SsrIDFactory();
var SsrIDFactory$1 = SsrIDFactory.Default;

/**
 * The `UniqueIDProvider` component is how Wonder Blocks components obtain
 * unique identifiers. This component ensures that server-side rendering and
 * initial client rendering match while allowing the provision of unique
 * identifiers for the client.
 *
 * In all but the first render, the children are rendered with the same
 * `IIdentifierFactory` instance, ensuring that the same calls will return the
 * same identifiers.
 *
 * The `get` method of the identifier factory ensures that the same identifier
 * is returned for like requests, but also that all identifiers provided are
 * unique. Therefore, `get("test")` will always equal `get("test")`, and
 * `get("test2")` will always equal `get("test2")`, but `get("test")` will
 * never equal `get("test2")`.
 */
class UniqueIDProvider extends Component {
  _performRender(firstRender) {
    const {
      children,
      mockOnFirstRender,
      scope
    } = this.props; // If this is our first render, we're going to stop right here.
    // Note: `firstRender` will be `false` on the first render if this
    // component is a descendant of a `WithSSRPlaceholder`.

    if (firstRender) {
      if (mockOnFirstRender) {
        // We're allowing an initial render, so let's pass our mock
        // identifier factory to support SSR.
        return children(SsrIDFactory$1);
      }

      return null;
    } // Create an identifier factory if we don't already have one


    if (!this._idFactory) {
      this._idFactory = new UniqueIDFactory(scope);
    } // It's a regular render, so let's use our identifier factory.


    return children(this._idFactory);
  }

  render() {
    // Here we use the WithSSRPlaceholder component to control
    // when we render and whether we provide a mock or real
    // identifier factory.
    return /*#__PURE__*/createElement(WithSSRPlaceholder, {
      placeholder: () => this._performRender(true)
    }, () => this._performRender(false));
  }

}

/**
 * This is a wrapper that returns an identifier. If the `id` prop is set, the component will
 * return the same id to be consumed by its children. Otherwise, a unique id will be provided.
 * This is beneficial for accessibility purposes, among other things.
 *
 * The main difference with UniqueIDProvider is that IDProvider has a single responsibility,
 * to return an identifier that can by used by the children that are rendered internally.
 *
 * This way, the wrapped component will receive this custom ID and will use it to connect
 * different elements.
 *
 * e.g. It uses the same generated id to connect a Dialog with its main title, or form label
 * with the associated input element, etc.
 */
class IDProvider extends Component {
  renderChildren(ids) {
    const {
      id,
      children
    } = this.props;
    const uniqueId = ids ? ids.get(IDProvider.defaultId) : id;

    if (!uniqueId) {
      throw new Error("Did not get an identifier factory nor a id prop");
    }

    return children(uniqueId);
  }

  render() {
    const {
      id,
      scope
    } = this.props;

    if (id) {
      // Let's bypass the extra weight of an id provider since we don't
      // need it.
      return this.renderChildren();
    } else {
      return /*#__PURE__*/createElement(UniqueIDProvider, {
        scope: scope,
        mockOnFirstRender: true
      }, ids => this.renderChildren(ids));
    }
  }

}
IDProvider.defaultId = "wb-id";

let _Symbol$iterator;

/**
 * Utility for finding the scroll ancestors of a child
 *
 * Inspired by https://github.com/olahol/scrollparent.js
 * with modifications to make an iterator that returns a sequence of all scroll
 * ancestors.
 *
 * Also modified for our standards (and commented a bit).
 */
const getElementStyle = function getElementStyle(node, prop) {
  return getComputedStyle(node).getPropertyValue(prop);
};

const getElementOverflow = function getElementOverflow(node) {
  return getElementStyle(node, "overflow") + getElementStyle(node, "overflow-y") + getElementStyle(node, "overflow-x");
};

const canScroll = function canScroll(node) {
  return /(auto|scroll)/.test(getElementOverflow(node));
}; // NOTE(somewhatabstract): Flow includes the @@iterator value in the Iterator
// interface definition, but it doesn't pick up on our computed property
// defining it (tried handling this in other ways, but it just won't work).
// So, we need this and the additional FlowFixMe a few lines down on the
// iterator property itself.
// $FlowFixMe[prop-missing]


_Symbol$iterator = Symbol.iterator;

class ScrollAncestorsIterator {
  constructor(element) {
    this.done = false;

    if (!(element instanceof HTMLElement)) {
      this.done = true;
    } else {
      this.parentElement = element.parentElement;
    }
  } //$FlowFixMe: Computed property keys are not allowed by flow at the moment


  [_Symbol$iterator]() {
    return this;
  }

  next() {
    if (this.done) {
      return {
        done: true
      };
    } // Climb the DOM looking for the next scroll candidate.


    let scrollCandidate;

    do {
      scrollCandidate = this.parentElement;
      this.parentElement = this.parentElement && this.parentElement.parentElement;
    } while (scrollCandidate && !canScroll(scrollCandidate));

    if (!scrollCandidate) {
      // If we don't have a scroll candidate, we'll definitely be done
      // iterating by the next call to next().
      // So let's remember that.
      this.done = true; // If we don't have a documentElement, we are actually done right
      // now, rather than on the next call.

      if (!document.documentElement) {
        return {
          done: true
        };
      } // Otherwise, as we have a documentElement, this is our penultimate
      // iteration .


      return {
        done: false,
        value: document.documentElement
      };
    } // We found a scroll ancestor, so let's return that.


    return {
      done: false,
      value: scrollCandidate
    };
  }

}

function enumerateScrollAncestors(element) {
  return Object.defineProperty({}, Symbol.iterator, {
    value: () => new ScrollAncestorsIterator(element),
    writable: true
  });
}

function getAxisIntersection(intersectingRect, boundsRect, axis) {
  const start = rect => axis === "horizontal" ? rect.left : rect.top;

  const end = rect => axis === "horizontal" ? rect.right : rect.bottom;

  if (end(intersectingRect) <= start(boundsRect)) {
    return "before";
  } else if (start(intersectingRect) >= end(boundsRect)) {
    return "after";
  }

  return "within";
}

function UndeterminedIntersection() {
  return {
    horizontal: null,
    vertical: null
  };
}

function FullIntersection() {
  return {
    horizontal: "within",
    vertical: "within"
  };
}
/**
 * Determine if an element intersects a single other element.
 *
 * It is assumed that the element provided as `boundsElement` is an element that
 * could obscure the given element, `element`.
 */


function getElementIntersectionAgainstParent(intersectingRect, boundsElement) {
  // We need to check that it matters if we're out of bounds. If the parent
  // element isn't clipping or otherwise hiding things outside its bounds,
  // then checking against bounds isn't going to be much use.
  // So, let's get the style for the element and use the overflow values.
  const style = boundsElement.currentStyle || window.getComputedStyle(boundsElement);
  const boundingRect = boundsElement.getBoundingClientRect();
  const boundsRect = {
    top: boundingRect.top,
    bottom: boundingRect.bottom,
    left: boundingRect.left,
    right: boundingRect.right
  }; // In webapp we set height: 100% on html, body and overflow-y: scroll on body.
  // This results in the height reported by getBoundingClientRect being the height
  // of the viewport instead of the height of the page.  We use the scrollHeight
  // of the body to corect the bounds.
  // TODO(kevinb): screenshot test this

  if (boundsElement === document.body) {
    boundsRect.bottom = boundsRect.top + boundsElement.scrollHeight;
  } // We assume we're within this specific bounds element if it's overflow is
  // visible.


  const horizontal = style.overflowX === "visible" ? "within" : getAxisIntersection(intersectingRect, boundsRect, "horizontal"); // We assume we're within this specific bounds element if it's overflow is
  // visible.

  const vertical = style.overflowY === "visible" ? "within" : getAxisIntersection(intersectingRect, boundsRect, "vertical");
  return {
    horizontal,
    vertical
  };
}
/**
 * Determine if a given element intersects with the visible bounds of its
 * scroll parents, or the bounds of a specific element.
 */


function getElementIntersection(element, boundsElement) {
  if (!element) {
    // An non-existant element is definitely not visible.
    return UndeterminedIntersection();
  }

  const {
    top,
    left,
    bottom,
    right
  } = element.getBoundingClientRect();
  const intersectingRect = {
    top,
    left,
    bottom,
    right
  }; // If we're looking against a single boundary element, then we just do that.

  if (boundsElement) {
    return getElementIntersectionAgainstParent(intersectingRect, boundsElement);
  }

  if (element instanceof Element) {
    // Otherwise, we enumerate the scroll parents and test against those.
    // If one of them is hiding our candidate element, then we will return.
    for (const scrollParent of enumerateScrollAncestors(element)) {
      const intersection = getElementIntersectionAgainstParent(intersectingRect, scrollParent); // If the intersectingRect is before or after the parent in one or both
      // dimensions, then return our intersection result. Otherwise, we'll
      // keep on searching up our parents.

      if (intersection.vertical !== "within" || intersection.horizontal !== "within") {
        // Stop looking, we've found something that is hiding the element.
        return intersection;
      }
    }
  } // If we got here, the element is within the bounds of its parents.


  return FullIntersection();
}

let serverSide = false;
var server = {
  /**
   * Check if we are running in server-side mode.
   *
   * @returns {boolean} `true` if we are in server-side mode; otherwise,
   * `false`
   */
  isServerSide: () => serverSide,

  /**
   * Set server-side mode to true.
   */
  setServerSide: () => {
    serverSide = true;
  }
};

export { IDProvider, server as Server, Text, UniqueIDProvider, View, WithSSRPlaceholder, addStyle, getElementIntersection };
